import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@razorpay/blade/components';

import { Input } from 'components/ui/Form/Input';
import { StandardButton } from 'components/ui/Button';
import permissions from 'constants/permissions';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import MultiSelect from 'components/ui/Dropdown/MultiSelect';
import { CustomFieldFilter } from 'components/CustomFieldFilter';
import { CustomFieldFilterToggleButton } from 'components/CustomFieldFilter/FilterToggleButton';
import {
  ResetBladePortalOverridingStyles,
  ResetLocalOverridingStyles,
} from 'components/RunPayroll/ComplianceActions/styles';
import { OrgLocation } from '../utils/types';
import { useRunPayrollFilterContext } from '../contexts/RunPayrollFiltersContext';
import closeIcon from 'assets/icon--close-blue.svg';
import searchIcon from 'assets/icon--search-white.svg';
import { trackXPayrollEvent } from 'utils/analytics';
import { getStaticMediaUrl } from 'utils/Urls';

import styles from './index.module.scss';

const RunPayrollFilters = ({
  isLoading,
  isBulkActionsEnabled,
  onCustomFieldFilterLoadingChange,
}: {
  isLoading: boolean;
  isBulkActionsEnabled: boolean;
  onCustomFieldFilterLoadingChange: (isLoading: boolean) => void;
}) => {
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [isCustomFieldFilterOpen, setIsCustomFieldFilterOpen] = useState(false);
  const [customFieldFilterCount, setCustomFieldFilterCount] = useState(0);

  const departments = useSelector(loggedInUserSelectors.departments);
  const locations = useSelector(loggedInUserSelectors.locations);
  const features = useSelector(loggedInUserSelectors.features);
  const userPermissions = useSelector(loggedInUserSelectors.permissions);
  const canManageView = userPermissions.includes(permissions.MANAGE_CUSTOM_FIELDS);
  const filters = useRunPayrollFilterContext();

  const appliedFilters = filters?.filters;

  const onSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    filters?.setSearchPhraseFilter(searchFilter);
  };

  const setDepartments = (options: (string | number)[]) => {
    trackXPayrollEvent(`runpayroll.departments_filtered`);
    filters?.setDepartmentsFilter(options as string[]);
  };

  const setLocations = (options: (string | number)[]) => {
    trackXPayrollEvent(`runpayroll.locations_filtered`);
    filters?.setLocationsFilter(options as string[]);
  };

  const onClearSearch = () => {
    setSearchFilter('');
    if (!!appliedFilters?.searchPhrase) {
      filters?.setSearchPhraseFilter('');
    }
  };

  const isHrisCustomFieldsEnabled = Boolean(features?.hrisCustomFields);

  const toggleCustomFieldFilter = () => setIsCustomFieldFilterOpen((isOpen) => !isOpen);

  useEffect(() => {
    if (appliedFilters?.searchPhrase !== searchFilter) {
      setSearchFilter(appliedFilters?.searchPhrase ?? '');
    }
  }, [appliedFilters?.searchPhrase]);

  const departmentsOptions = departments?.map((department) => ({
    key: department,
    value: department,
  }));
  const locationOptions = (locations as OrgLocation[])?.map((location) => ({
    key: location.code.toString(),
    value: location.name,
  }));

  return (
    <div
      className={`grid grid-cols-10 gap-6 ${
        appliedFilters?.departments.length ||
        appliedFilters?.locations.length ||
        isCustomFieldFilterOpen
          ? 'mb-10'
          : 'mb-20'
      } w-full`}>
      {isHrisCustomFieldsEnabled && <ResetBladePortalOverridingStyles />}
      <div className={`col-span-10 ${isBulkActionsEnabled ? 'lg:col-span-4' : ''}`}>
        <form className={styles['run-payroll-search']}>
          <Input
            type="search"
            name="search"
            placeholder="Search by name, email or employee id"
            value={searchFilter}
            disabled={isLoading}
            onChange={(event) => setSearchFilter(event.target.value)}
          />
          {!!searchFilter && (
            <img
              className={styles['runpayroll-filter-search-clear']}
              src={getStaticMediaUrl(closeIcon)}
              alt=""
              onClick={onClearSearch}
            />
          )}
          <StandardButton
            onClick={onSubmit}
            disabled={isLoading}
            className={styles['run-payroll-data-search']}>
            <img width="30px" src={getStaticMediaUrl(searchIcon)} alt="Search" />
          </StandardButton>
        </form>
      </div>
      {isBulkActionsEnabled && (
        <>
          <div className="col-span-10 md:col-span-5 lg:col-span-3">
            <MultiSelect
              id="department-filter"
              options={departmentsOptions ?? []}
              name={'Department'}
              value={appliedFilters?.departments}
              onChange={setDepartments}
              disableChipsDisplay={true}
              disableAllOption={true}
              disabled={isLoading}
            />
          </div>
          <Box
            gridColumn={{ base: 'span 10', m: 'span 5', l: 'span 3' }}
            display="flex"
            gap="7.5px"
            alignItems="flex-start">
            <Box flex="1">
              <MultiSelect
                id="locations-filter"
                options={locationOptions ?? []}
                value={appliedFilters?.locations}
                name={'Location'}
                onChange={setLocations}
                disableChipsDisplay={true}
                disableAllOption={true}
                disabled={isLoading}
              />
            </Box>
            {isHrisCustomFieldsEnabled && (
              <CustomFieldFilterToggleButton
                isOpen={isCustomFieldFilterOpen}
                isDisabled={isLoading}
                appliedCount={customFieldFilterCount}
                accessibilityLabel="Run payroll custom field filters"
                onToggle={toggleCustomFieldFilter}
              />
            )}
          </Box>
        </>
      )}
      {isHrisCustomFieldsEnabled && !isBulkActionsEnabled && (
        <Box gridColumn="span 10">
          <CustomFieldFilterToggleButton
            isOpen={isCustomFieldFilterOpen}
            isDisabled={isLoading}
            appliedCount={customFieldFilterCount}
            accessibilityLabel="Run payroll custom field filters"
            onToggle={toggleCustomFieldFilter}
          />
        </Box>
      )}
      {isHrisCustomFieldsEnabled && (
        <Box gridColumn="span 10" display={isCustomFieldFilterOpen ? 'block' : 'none'}>
          <ResetLocalOverridingStyles>
            <CustomFieldFilter
              disabled={isLoading}
              canManageView={canManageView}
              onApply={(result) => filters?.setPeopleIdsFilter(result.peopleIds)}
              onClear={() => filters?.setPeopleIdsFilter(null)}
              onLoadingChange={onCustomFieldFilterLoadingChange}
              onSelectedFiltersCountChange={setCustomFieldFilterCount}
            />
          </ResetLocalOverridingStyles>
        </Box>
      )}
    </div>
  );
};

export default RunPayrollFilters;
